/**
 * DENET — ürün karşılaştırma
 *
 * Kartlardaki onay kutusu seçimi biriktirir, altta yapışkan bir çubuk açılır,
 * çubuktan açılan pencere tabloyu kurar.
 *
 * Tablo sunucuda üretilmiyor: satır kümesi seçilen ürünlere göre değişiyor.
 * Her ürün `/products/<handle>?view=compare` ile bir JSON olarak alınıyor,
 * satırlar birleşimden çıkarılıyor — hiçbir üründe olmayan özellik hiç
 * satır olmuyor.
 *
 * Seçim sessionStorage'da: sekme kapanınca unutulur. Karşılaştırma o anki
 * alışverişe ait bir iş, kalıcı bir liste değil.
 */

import { DenetElement, define, qsa, trapFocus, lockScroll, unlockScroll } from '@denet/core';

const KEY = 'denet:compare';
const MAX = 4;

const read = () => {
  try {
    const raw = JSON.parse(sessionStorage.getItem(KEY) || '[]');
    return Array.isArray(raw) ? raw.filter((h) => typeof h === 'string').slice(0, MAX) : [];
  } catch {
    return [];
  }
};

/** @param {string[]} list */
const write = (list) => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
  } catch {
    /* yazılamıyorsa seçim yalnızca bu sayfada yaşar */
  }
  document.dispatchEvent(new CustomEvent('denet:compare:change', { detail: { list } }));
};

class DnCompare extends DenetElement {
  mounted() {
    this.bar = this.querySelector('[data-ref="bar"]');
    this.slots = this.querySelector('[data-ref="slots"]');
    this.countEl = this.querySelector('[data-ref="count"]');
    this.openBtn = this.querySelector('[data-ref="open"]');
    this.clearBtn = this.querySelector('[data-ref="clear"]');
    this.dialog = this.querySelector('dialog');
    this.body = this.querySelector('[data-ref="body"]');

    /* Kartlar sayfaya sonradan da gelebiliyor (ray, sonsuz liste, son
       baktıkların). Onay kutusunu tek tek bağlamak yerine belgeye tek bir
       dinleyici koyup olayı yakalıyoruz. */
    document.addEventListener('change', (event) => {
      const input = /** @type {HTMLInputElement} */ (event.target);
      if (!input.matches?.('[data-compare]')) return;
      this.#toggle(input.dataset.compare || '', input.checked);
    }, { signal: this.signal });

    document.addEventListener('denet:compare:change', () => this.#render(), { signal: this.signal });

    this.openBtn?.addEventListener('click', () => this.#open(), { signal: this.signal });
    this.clearBtn?.addEventListener('click', () => write([]), { signal: this.signal });

    this.querySelector('[data-ref="close"]')?.addEventListener('click', () => this.dialog?.close(), {
      signal: this.signal,
    });

    this.#render();
  }

  /**
   * @param {string} handle
   * @param {boolean} on
   */
  #toggle(handle, on) {
    if (!handle) return;
    let list = read();

    if (on) {
      if (list.includes(handle)) return;
      if (list.length >= MAX) {
        /* Sınır dolu: kutuyu geri kaldır, sessizce yutma. */
        const input = document.querySelector(`[data-compare="${handle}"]`);
        if (input instanceof HTMLInputElement) input.checked = false;
        this.#flash();
        return;
      }
      list = [...list, handle];
    } else {
      list = list.filter((h) => h !== handle);
    }

    write(list);
  }

  #flash() {
    this.bar?.classList.add('is-full');
    window.setTimeout(() => this.bar?.classList.remove('is-full'), 900);
  }

  #render() {
    const list = read();

    /* Sayfadaki kutular listeyle aynı fikirde olmalı: başka sayfada seçilen
       ürün buraya gelindiğinde işaretli görünsün. */
    qsa('[data-compare]').forEach((el) => {
      const input = /** @type {HTMLInputElement} */ (el);
      input.checked = list.includes(input.dataset.compare || '');
    });

    if (this.countEl) this.countEl.textContent = String(list.length);
    if (this.openBtn) this.openBtn.disabled = list.length < 2;

    if (this.slots) {
      this.slots.innerHTML = list
        .map((h) => `<li class="dn-compare__slot"><button type="button" data-remove="${h}" aria-label="${h} kaldır">${h.replace(/-/g, ' ')}<span aria-hidden="true">×</span></button></li>`)
        .join('');

      qsa('[data-remove]', this.slots).forEach((btn) => {
        btn.addEventListener('click', () => this.#toggle(btn.getAttribute('data-remove') || '', false));
      });
    }

    this.classList.toggle('is-visible', list.length > 0);
  }

  async #open() {
    const list = read();
    if (list.length < 2 || !this.dialog || !this.body) return;

    this.body.innerHTML = `<p class="dn-compare__loading">${this.dataset.loading || 'Yükleniyor'}…</p>`;
    this.dialog.showModal();
    lockScroll();
    this.release = trapFocus(this.dialog);

    this.dialog.addEventListener('close', () => {
      unlockScroll();
      this.release?.();
    }, { once: true });

    const root = window.Shopify?.routes?.root || '/';
    const items = await Promise.all(
      list.map(async (handle) => {
        try {
          const res = await fetch(`${root}products/${handle}?view=compare`);
          return res.ok ? await res.json() : null;
        } catch {
          return null;
        }
      })
    );

    this.body.innerHTML = this.#table(items.filter(Boolean));
  }

  /** @param {any[]} items */
  #table(items) {
    if (items.length === 0) return '<p class="dn-compare__loading">Ürün bilgisi alınamadı.</p>';

    /* Satırlar birleşimden: hiçbir üründe dolu olmayan özellik hiç yazılmaz. */
    const keys = [];
    for (const item of items) {
      for (const [key, value] of Object.entries(item.specs || {})) {
        if (value && !keys.includes(key)) keys.push(key);
      }
    }

    const esc = (s) => String(s ?? '').replace(/[<>&"]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c]));

    const head = items
      .map(
        (p) => `<th scope="col">
          <a class="dn-compare__card" href="${esc(p.url)}">
            ${p.image ? `<img src="${esc(p.image)}" alt="" width="120" height="120" loading="lazy">` : '<span class="dn-compare__noimg"></span>'}
            ${p.vendor ? `<span class="dn-compare__vendor">${esc(p.vendor)}</span>` : ''}
            <span class="dn-compare__title">${esc(p.title)}</span>
          </a>
          <span class="dn-compare__price${p.onSale ? ' is-sale' : ''}">${esc(p.price)}${p.compareAt ? `<s>${esc(p.compareAt)}</s>` : ''}</span>
          <span class="dn-compare__tax">${esc(this.dataset.tax || '')}</span>
        </th>`
      )
      .join('');

    /* Farklı olan satırlar işaretlenir: karşılaştırmanın işi farkı göstermek. */
    const rows = keys
      .map((key) => {
        const values = items.map((p) => p.specs?.[key] || '—');
        const differs = new Set(values).size > 1;
        return `<tr${differs ? ' class="is-diff"' : ''}>
          <th scope="row">${esc(key)}</th>
          ${values.map((v) => `<td>${esc(v)}</td>`).join('')}
        </tr>`;
      })
      .join('');

    /* Sabit satırlar da fark kontrolünden geçer: biri stokta biri tükenmişse
       o satır da vurgulanmalı — ilk sürümde bu iki satır kontrolün dışındaydı. */
    const fixedRow = (label, values) => {
      const differs = new Set(values).size > 1;
      return `<tr${differs ? ' class="is-diff"' : ''}><th scope="row">${label}</th>${values
        .map((v) => `<td>${v}</td>`)
        .join('')}</tr>`;
    };

    const stock = fixedRow(
      'Stok',
      items.map((p) =>
        p.available
          ? '<span class="dn-stock">Stokta</span>'
          : '<span class="dn-stock dn-stock--out">Tükendi</span>'
      )
    );

    const sku = fixedRow('Stok kodu', items.map((p) => esc(p.sku || '—')));

    return `<div class="dn-compare__scroll">
      <table class="dn-compare__table">
        <thead><tr><td></td>${head}</tr></thead>
        <tbody>${sku}${stock}${rows}</tbody>
      </table>
    </div>`;
  }
}

define('dn-compare', DnCompare);
